<template>
  <hb-section :title="$t('influencer_panel.tab_links')" :subtitle="summary">
    <template #actions>
      <b-button variant="primary" size="sm" @click="$emit('generate-link')">
        <feather-icon icon="PlusIcon" size="14" class="mr-50" />
        {{ $t('influencer_panel.generate_link') }}
      </b-button>
    </template>

    <hb-empty-state v-if="!links.length" :title="$t('influencer_panel.no_links')" icon="LinkIcon" />

    <div v-else>
      <div v-for="link in links" :key="link.id" class="links__row">
        <div class="links__identity">
          <div class="links__name">{{ linkName(link) }}</div>
          <div class="links__url">
            <feather-icon icon="LinkIcon" size="13" />
            <code>{{ link.short_url }}</code>
          </div>
          <div v-if="couponCode(link)" class="links__coupon">
            <feather-icon icon="TagIcon" size="13" />
            {{ $t('influencer_panel.link_coupon_note', { code: couponCode(link) }) }}
          </div>
        </div>

        <div class="links__stat">
          <div class="links__stat-label">{{ $t('influencer_panel.clicks') }}</div>
          <div class="links__stat-value">{{ Number(link.clicks_count || 0).toLocaleString('pt-BR') }}</div>
        </div>

        <div class="links__stat">
          <div class="links__stat-label">{{ $t('influencer_panel.sales') }}</div>
          <div class="links__stat-value">{{ link.conversions || 0 }}</div>
        </div>

        <div class="links__stat">
          <div class="links__stat-label">{{ $t('influencer_panel.conversion_rate') }}</div>
          <div class="links__stat-value">
            {{ Number(link.conversion_rate || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) }}%
          </div>
        </div>

        <div class="links__actions">
          <hb-copy-button :text="link.short_url" :share-title="linkName(link)" />
        </div>
      </div>

      <!--
        Venda por link sai da atribuicao por clique. A venda por cupom passou a carregar
        o clique quando ele e identificavel (hb_ref presente); sem isso — codigo digitado
        de ouvido ou comprador sem cookies — a venda existe na comissao mas nao pertence
        a link algum, e sem este aviso a soma dos links parece contradizer o total do topo.
      -->
      <p class="links__note">
        <feather-icon icon="InfoIcon" size="13" />
        {{ $t('influencer_panel.links_sales_note') }}
      </p>
    </div>
  </hb-section>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import HbCopyButton from '@/components/hubee/HbCopyButton.vue'

/** Links rastreaveis do influencer, com o desempenho de cada um. */
export default {
  name: 'MyLinksPane',
  components: {
    BButton, HbSection, HbEmptyState, HbCopyButton,
  },
  props: {
    links: { type: Array, default: () => [] },
  },
  computed: {
    summary() {
      const clicks = this.links.reduce((sum, link) => sum + Number(link.clicks_count || 0), 0)

      return `${this.$t('influencer_panel.links_count', { count: this.links.length })} · ${this.$t('influencer_panel.clicks_count', { count: clicks.toLocaleString('pt-BR') })}`
    },
  },
  methods: {
    /** Link de produto mostra o produto; link da campanha inteira mostra a campanha. */
    linkName(link) {
      if (link.product && link.product.name) return link.product.name

      const campaign = link.participation && link.participation.campaign

      return (campaign && campaign.name) || this.$t('influencer_panel.whole_campaign')
    },
    /** So anuncia o cupom que o link realmente aplica: pausado/vencido fica de fora. */
    couponCode(link) {
      const participation = link.participation || {}
      const coupon = participation.coupon

      if (coupon && coupon.status !== 'AC') return null

      return participation.coupon_code || null
    },
  },
}
</script>

<style scoped>
.links__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 0.75rem;
  border-bottom: 1px solid var(--border-color);
  transition: background-color var(--dur-fast) var(--ease-in-out);
}

.links__row:hover {
  background: var(--surface-hover);
}

.links__identity {
  flex: 1 1 240px;
  min-width: 0;
}

.links__name {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.links__url {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--fs-sm);
  color: var(--text-muted);
  word-break: break-all;
}

.links__coupon {
  margin-top: 0.2rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.links__stat {
  flex: 0 0 auto;
  min-width: 78px;
}

.links__stat-label {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.links__stat-value {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.links__actions {
  flex: 0 0 auto;
  margin-left: auto;
}

.links__note {
  margin: 1rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}
</style>
